import React, { useEffect, useState } from "react";
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  Timestamp,
} from "../../../config/dataClient";
import { FlaskConical, Plus, Trash2, RefreshCw } from "lucide-react";
import { db } from "../../../config/firebase";
import { APP_ID, ROLES } from "../../../config/constants";

const TEST_UIDS = {
  FT: "TEST_FT_001",
  PT_FULL: "TEST_PT_FULL_001",
  PT_NODATA: "TEST_PT_NODATA_001",
};

const NAMES = {
  [TEST_UIDS.FT]: "測試正職(下月滿週年)",
  [TEST_UIDS.PT_FULL]: "測試工讀(滿週年+有資料)",
  [TEST_UIDS.PT_NODATA]: "測試工讀(滿週年無資料)",
};

// 建立 profile + daily_status 共用骨架
const baseProfile = (uid, role, extra = {}) => ({
  name: NAMES[uid],
  fullName: NAMES[uid],
  email: `${uid.toLowerCase()}@test.local`,
  role,
  accountStatus: "active",
  isProfileFilled: true,
  isHidden: false,
  photoURL: null,
  birthday: "2000-01-01",
  ...extra,
});

const baseDailyStatus = (uid, role, extra = {}) => ({
  uid,
  name: NAMES[uid],
  fullName: NAMES[uid],
  photoURL: null,
  status: "offline",
  isHidden: false,
  accountStatus: "active",
  role,
  birthday: "2000-01-01",
  ...extra,
});

// 寫一個 user (profile + daily_status)
const writeUser = async (uid, profileData, dailyData) => {
  await setDoc(
    doc(db, "artifacts", APP_ID, "users", uid, "profile", "main"),
    profileData
  );
  await setDoc(
    doc(db, "artifacts", APP_ID, "public", "data", "daily_status", uid),
    dailyData
  );
};

// 產生工作日打卡 logs 並用 batch 寫入
// startDate, endDate (exclusive) — 寫入每個 Mon~Fri 9:00 IN / 18:00 OUT
const seedWorkdayLogs = async (uid, startDate, endDate) => {
  const logsCol = collection(db, "artifacts", APP_ID, "users", uid, "logs");
  let batch = writeBatch(db);
  let opCount = 0;
  const cursor = new Date(startDate);
  while (cursor < endDate) {
    const dow = cursor.getDay();
    // 1~5 = Mon~Fri
    if (dow >= 1 && dow <= 5) {
      const inDate = new Date(cursor);
      inDate.setHours(9, 0, 0, 0);
      const outDate = new Date(cursor);
      outDate.setHours(18, 0, 0, 0);

      const inRef = doc(logsCol);
      const outRef = doc(logsCol);
      batch.set(inRef, {
        uid,
        type: "in",
        timestamp: Timestamp.fromDate(inDate),
        device: "seed",
        ip: "seed",
        userName: NAMES[uid],
        seed: true,
      });
      batch.set(outRef, {
        uid,
        type: "out",
        timestamp: Timestamp.fromDate(outDate),
        device: "seed",
        ip: "seed",
        userName: NAMES[uid],
        seed: true,
      });
      opCount += 2;
      if (opCount >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        opCount = 0;
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  if (opCount > 0) await batch.commit();
};

// 為了讓 generateSlip 跑得起來 (stats.hasLogs)，
// 在「當前要查看的薪資月份」也補一筆 9:00-18:00
const seedCurrentMonthLog = async (uid, viewYear, viewMonth0) => {
  const logsCol = collection(db, "artifacts", APP_ID, "users", uid, "logs");
  const inDate = new Date(viewYear, viewMonth0, 15, 9, 0, 0);
  const outDate = new Date(viewYear, viewMonth0, 15, 18, 0, 0);
  await setDoc(doc(logsCol), {
    uid, type: "in", timestamp: Timestamp.fromDate(inDate),
    device: "seed", ip: "seed", userName: NAMES[uid], seed: true,
  });
  await setDoc(doc(logsCol), {
    uid, type: "out", timestamp: Timestamp.fromDate(outDate),
    device: "seed", ip: "seed", userName: NAMES[uid], seed: true,
  });
};

// 刪除一個測試 user 的所有資料（logs + daily_status + profile + 相關薪資單）
const deleteOneTestUser = async (uid) => {
  // 1. 刪 logs
  const logsSnap = await getDocs(collection(db, "artifacts", APP_ID, "users", uid, "logs"));
  let batch = writeBatch(db);
  let n = 0;
  for (const d of logsSnap.docs) {
    batch.delete(d.ref);
    n++;
    if (n >= 400) { await batch.commit(); batch = writeBatch(db); n = 0; }
  }
  if (n > 0) await batch.commit();

  // 2. 刪薪資單（payroll_slips 內所有以該 uid 結尾的）
  const slipsSnap = await getDocs(
    query(collection(db, "artifacts", APP_ID, "public", "data", "payroll_slips"), where("uid", "==", uid))
  );
  for (const d of slipsSnap.docs) await deleteDoc(d.ref);

  // 3. 刪 daily_status
  await deleteDoc(doc(db, "artifacts", APP_ID, "public", "data", "daily_status", uid));

  // 4. 刪 profile
  await deleteDoc(doc(db, "artifacts", APP_ID, "users", uid, "profile", "main"));
};

export default function TestDataPanel({ showToast, confirm }) {
  const [status, setStatus] = useState({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");

  const today = new Date();
  // 預設 PayrollView 顯示「上個月」薪資 → 把工讀生到職日月份對齊「上個月」
  const viewMonth0 = today.getMonth() - 1 < 0 ? 11 : today.getMonth() - 1;
  const viewYear = today.getMonth() - 1 < 0 ? today.getFullYear() - 1 : today.getFullYear();
  // 正職下月滿週年 = 本月（因為使用者預設看上個月，下個月就是本月）
  const ftAnnivMonth0 = today.getMonth();
  const ftAnnivYear = today.getFullYear();

  // PT 到職日：兩年前同月份的 15 號（讓「滿 2 年」剛好落在 viewMonth0）
  const ptHireDate = `${viewYear - 2}-${String(viewMonth0 + 1).padStart(2, "0")}-15`;
  // FT 到職日：兩年前 + 下月份 15 號（讓「滿 2 年」剛好落在 ftAnnivMonth0）
  const ftHireDate = `${ftAnnivYear - 2}-${String(ftAnnivMonth0 + 1).padStart(2, "0")}-15`;

  const refreshStatus = async () => {
    const s = {};
    for (const uid of Object.values(TEST_UIDS)) {
      const snap = await getDoc(doc(db, "artifacts", APP_ID, "public", "data", "daily_status", uid));
      s[uid] = snap.exists();
    }
    setStatus(s);
  };
  useEffect(() => { refreshStatus(); }, []);

  const createFT = async () => {
    if (busy) return;
    setBusy(true);
    setProgress("建立 FT 測試員工...");
    try {
      await writeUser(
        TEST_UIDS.FT,
        baseProfile(TEST_UIDS.FT, ROLES.FULLTIME, {
          startDate: ftHireDate,
          baseSalary: 40000,
          hourlyRate: 0,
          laborInsurance: 800,
          healthInsurance: 600,
          annualLeaveRemaining: 7,
          annualLeaveUsed: 0,
        }),
        baseDailyStatus(TEST_UIDS.FT, ROLES.FULLTIME)
      );
      // 補當月打卡讓薪資單能生成
      setProgress("補當月打卡...");
      await seedCurrentMonthLog(TEST_UIDS.FT, viewYear, viewMonth0);
      await refreshStatus();
      showToast(`已建立 ${NAMES[TEST_UIDS.FT]}（到職 ${ftHireDate}）`);
    } catch (e) { alert("建立失敗：" + e.message); }
    finally { setBusy(false); setProgress(""); }
  };

  const createPTFull = async () => {
    if (busy) return;
    setBusy(true);
    try {
      setProgress("建立 PT 員工資料...");
      await writeUser(
        TEST_UIDS.PT_FULL,
        baseProfile(TEST_UIDS.PT_FULL, ROLES.PARTTIME, {
          startDate: ptHireDate,
          hourlyRate: 200,
          laborInsurance: 277,
          healthInsurance: 443,
        }),
        baseDailyStatus(TEST_UIDS.PT_FULL, ROLES.PARTTIME)
      );

      // 上上年區間 [hire, hire+1y) — 剛滿 2 年時的上上年 = 第 1 年工作期
      setProgress("寫入上上年打卡 (Mon-Fri × 12 個月)...");
      const hireDate = new Date(ptHireDate);
      const priorEnd = new Date(hireDate);
      priorEnd.setFullYear(priorEnd.getFullYear() + 1);
      await seedWorkdayLogs(TEST_UIDS.PT_FULL, hireDate, priorEnd);

      // 當前查看月份補一筆讓 slip 能生成
      setProgress("補當月打卡...");
      await seedCurrentMonthLog(TEST_UIDS.PT_FULL, viewYear, viewMonth0);

      await refreshStatus();
      showToast(`已建立 ${NAMES[TEST_UIDS.PT_FULL]}（到職 ${ptHireDate}）`);
    } catch (e) { alert("建立失敗：" + e.message); }
    finally { setBusy(false); setProgress(""); }
  };

  const createPTNoData = async () => {
    if (busy) return;
    setBusy(true);
    setProgress("建立 PT 無資料測試員工...");
    try {
      await writeUser(
        TEST_UIDS.PT_NODATA,
        baseProfile(TEST_UIDS.PT_NODATA, ROLES.PARTTIME, {
          startDate: ptHireDate,
          hourlyRate: 200,
          laborInsurance: 277,
          healthInsurance: 443,
        }),
        baseDailyStatus(TEST_UIDS.PT_NODATA, ROLES.PARTTIME)
      );
      // 只在「當前查看月份」放一筆讓 slip 能生成；上上年完全空白
      await seedCurrentMonthLog(TEST_UIDS.PT_NODATA, viewYear, viewMonth0);
      await refreshStatus();
      showToast(`已建立 ${NAMES[TEST_UIDS.PT_NODATA]}（到職 ${ptHireDate}，無上上年資料）`);
    } catch (e) { alert("建立失敗：" + e.message); }
    finally { setBusy(false); setProgress(""); }
  };

  const deleteAll = async () => {
    const ok = await confirm({
      title: "刪除所有測試員工",
      message: "將刪除三位測試員工的 profile、daily_status、所有打卡 logs 及對應薪資單。此操作無法復原。",
      confirmText: "全部刪除",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      for (const [k, uid] of Object.entries(TEST_UIDS)) {
        setProgress(`刪除 ${k}...`);
        await deleteOneTestUser(uid);
      }
      await refreshStatus();
      showToast("所有測試員工已清除");
    } catch (e) { alert("刪除失敗：" + e.message); }
    finally { setBusy(false); setProgress(""); }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-amber-800 flex items-center gap-2 text-sm">
          <FlaskConical size={16} /> 測試工具（薪資年資功能）
        </h3>
        <button onClick={refreshStatus} disabled={busy} className="text-amber-600 hover:text-amber-800 p-1"><RefreshCw size={14}/></button>
      </div>
      <div className="text-[11px] text-amber-700 mb-3 leading-relaxed">
        建立預先安排好條件的假員工後，到「薪資」頁查看 → 上個月薪資頁應跳出對應提示。<br/>
        看完後請按「全部刪除」清乾淨，避免污染真實資料。
      </div>

      {busy && (
        <div className="bg-white/70 rounded-lg p-2 mb-3 text-xs text-amber-800 flex items-center gap-2">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-600"></div>
          {progress}
        </div>
      )}

      <div className="space-y-2">
        {/* FT */}
        <div className="bg-white rounded-lg p-2 border border-amber-100">
          <div className="flex justify-between items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-slate-700 truncate">
                🔵 {NAMES[TEST_UIDS.FT]}
                {status[TEST_UIDS.FT] && <span className="ml-1 text-green-600">●已存在</span>}
              </div>
              <div className="text-[10px] text-slate-500">到職 {ftHireDate} → {ftAnnivYear}/{String(ftAnnivMonth0+1).padStart(2,'0')} 滿 2 年</div>
            </div>
            <button onClick={createFT} disabled={busy || status[TEST_UIDS.FT]} className="bg-blue-500 text-white text-[10px] px-2 py-1.5 rounded font-bold disabled:bg-slate-300"><Plus size={10} className="inline"/> 建立</button>
          </div>
        </div>

        {/* PT Full */}
        <div className="bg-white rounded-lg p-2 border border-amber-100">
          <div className="flex justify-between items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-slate-700 truncate">
                🟢 {NAMES[TEST_UIDS.PT_FULL]}
                {status[TEST_UIDS.PT_FULL] && <span className="ml-1 text-green-600">●已存在</span>}
              </div>
              <div className="text-[10px] text-slate-500">到職 {ptHireDate} + 上上年 12 個月每個工作日打卡（約 500 筆）</div>
            </div>
            <button onClick={createPTFull} disabled={busy || status[TEST_UIDS.PT_FULL]} className="bg-emerald-500 text-white text-[10px] px-2 py-1.5 rounded font-bold disabled:bg-slate-300"><Plus size={10} className="inline"/> 建立</button>
          </div>
        </div>

        {/* PT No Data */}
        <div className="bg-white rounded-lg p-2 border border-amber-100">
          <div className="flex justify-between items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-slate-700 truncate">
                🟡 {NAMES[TEST_UIDS.PT_NODATA]}
                {status[TEST_UIDS.PT_NODATA] && <span className="ml-1 text-green-600">●已存在</span>}
              </div>
              <div className="text-[10px] text-slate-500">到職 {ptHireDate}，上上年無打卡 → 預期跳「無資料」提示</div>
            </div>
            <button onClick={createPTNoData} disabled={busy || status[TEST_UIDS.PT_NODATA]} className="bg-amber-500 text-white text-[10px] px-2 py-1.5 rounded font-bold disabled:bg-slate-300"><Plus size={10} className="inline"/> 建立</button>
          </div>
        </div>

        {/* Delete All */}
        <button
          onClick={deleteAll}
          disabled={busy || (!status[TEST_UIDS.FT] && !status[TEST_UIDS.PT_FULL] && !status[TEST_UIDS.PT_NODATA])}
          className="w-full bg-red-500 text-white text-xs py-2 rounded-lg font-bold mt-2 disabled:bg-slate-300 flex items-center justify-center gap-1"
        >
          <Trash2 size={14}/> 全部刪除（清乾淨）
        </button>
      </div>
    </div>
  );
}
